import {
  useStripe,
} from "@stripe/react-stripe-js";
import {
  Box, Button, Typography, Chip,
  Paper, InputBase, Divider, IconButton, CircularProgress, ToggleButtonGroup, ToggleButton,
  FormHelperText, ClickAwayListener, Dialog, DialogContent, useMediaQuery,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { dataConstants } from '../../../constants';
import { styled, useTheme } from '@mui/material/styles';
import { useRouter } from 'next/router';
import useAuth from '../../../hook/auth';
import { checkSubscription, createCheckout, timeout } from "../../../utils/helpers";
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import SellRoundedIcon from '@mui/icons-material/SellRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import PauseCircleRoundedIcon from '@mui/icons-material/PauseCircleRounded';
import * as send from '../../../../lib/analytics/send';
import { pricing as pricingTokens } from '../../../utils/designTokens';

// -----------------------------------------------------------------------------
// While this flag is `true`, clicking "Buy Subscription" shows a friendly
// dialog instead of starting checkout. The Stripe checkout flow below is left
// fully intact and untouched — flip this back to `false` to resume sign-ups.
// -----------------------------------------------------------------------------
const SUBSCRIPTIONS_PAUSED = true;

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  background: pricingTokens.toggle.bg,
  borderRadius: '12px',
  padding: '3px',
  '.MuiToggleButton-root.Mui-selected': {
    background: pricingTokens.toggle.selectedBg,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    color: pricingTokens.toggle.selectedText,
    borderRadius: '9px !important',
  },
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    lineHeight: 1,
    padding: '10px 20px',
    color: pricingTokens.toggle.text,
    fontWeight: 600,
    fontSize: '0.85rem',
    border: 'none',
    transition: 'all 0.2s ease',
  },
  '& .MuiToggleButton-root.Mui-selected:hover': {
    background: 'rgba(255,255,255,0.92)',
  },
  '& .MuiToggleButton-root:hover:not(.Mui-selected)': {
    background: 'rgba(255,255,255,0.1)',
  },
  '& .MuiToggleButtonGroup-grouped': {
    margin: 0,
    border: 0,
    '&.Mui-disabled': { border: 0 },
    '&:not(:first-of-type)': { borderRadius: '9px' },
    '&:first-of-type': { borderRadius: '9px' },
  },
}));

const { PLAN_CYCLES } = dataConstants || {};

const LandingCheckout = ({ pricePlans }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth() || {};
  const router = useRouter();
  const { reloadUserInfo } = user || {};
  const { customAttributes, email } = reloadUserInfo || {};
  const { userType, stripe_customer_id } = customAttributes ? JSON.parse(customAttributes) : {};
  const isPremium = reloadUserInfo?.customAttributes && (JSON.parse(reloadUserInfo?.customAttributes)?.userType === "PREMIUM" || JSON.parse(reloadUserInfo?.customAttributes)?.userType === "PROFESSIONAL");

  const [plan] = useState("Premium");
  const [plans] = useState(pricePlans ? pricePlans : (process.env.NODE_ENV !== 'production' ? dataConstants.DEVPLANS : dataConstants.PRODPLANS));
  const [planCycle, setPlanCycle] = useState(PLAN_CYCLES.ANNUALLY);
  const planItem = plans && plans.length > 0 ? plans?.find(d => d.plan.toLowerCase() === plan.toLowerCase()) : null;
  const [priceID, setPriceID] = useState(planItem?.ypid);
  const [priceIdLoading, setPriceIdLoading] = useState();
  const [currency_type, set_currency_type] = useState('usd');
  const [showCoupon, setShowCoupon] = useState(false);
  const [coupon, setCoupon] = useState({
    hasCode: false, code: '', loading: false, isValid: false,
  });
  const [hasPastSubscriptions, setPastSubscriptions] = useState(false);
  const [pausedDialogOpen, setPausedDialogOpen] = useState(false);

  const splitPriceMonthly = planItem?.m?.toString().split('.');

  let splitPriceQuarterly = planItem && currency_type === "inr" ? (coupon?.q_final_amount ?? planItem?.iq) / 3 :
    planItem && currency_type === "eur" ? (coupon?.q_final_amount ?? planItem?.eq) / 3 :
      planItem && currency_type === "gbp" ? (coupon?.q_final_amount ?? planItem?.gq) / 3 :
        (coupon?.q_final_amount ?? planItem?.q) / 3;
  splitPriceQuarterly = splitPriceQuarterly !== 0 ? (Number(splitPriceQuarterly)?.toFixed(2)?.toString().split('.')) : null;

  let splitPriceAnnually = (coupon?.y_final_amount ?? planItem?.y) / 12;
  if (coupon?.y_final_amount === 0) {
    splitPriceAnnually = [0, '00'];
  } else if (splitPriceAnnually === 0) {
    splitPriceAnnually = [0, '00'];
  } else {
    splitPriceAnnually = splitPriceAnnually !== 0 ? (Number(splitPriceAnnually)?.toFixed(2)?.toString().split('.')) : null;
  }

  useEffect(() => {
    const currentDate = new Date();
    if (`${currentDate}`.includes('India Standard Time')) {
      set_currency_type('inr');
    } else {
      set_currency_type('usd');
    }
  }, []);

  const stripe = useStripe();

  const handleBillingChange = (event, newCycle) => {
    setPriceID(dataConstants.PLAN_CYCLES.ANNUALLY === event.target.value ? planItem?.ypid :
      (dataConstants.PLAN_CYCLES.QUARTERLY === event.target.value ? planItem?.qpid : planItem?.mpid));
    if (newCycle !== null) {
      setPlanCycle(newCycle);
    }
  };

  const handleCycle = (event, newCycle) => {
    if (newCycle !== null) {
      setPlanCycle(newCycle);
    }
  };

  const handleCheckout = async (checkoutPriceID) => {
    if (!user) {
      return router.push({
        pathname: '/auth',
        query: { from: router.asPath },
      });
    }

    // Existing subscribers are routed to manage their plan — unaffected by the pause.
    if (isPremium) {
      return router.push('/profile');
    }

    // New sign-ups are paused — let the visitor know instead of starting checkout.
    if (SUBSCRIPTIONS_PAUSED) {
      setPausedDialogOpen(true);
      return;
    }

    setPriceIdLoading(checkoutPriceID);
    try {
      if (user && user?.email) {
        const { sessionId } = await createCheckout({
          url: '/api/create-checkout-session',
          priceID: checkoutPriceID,
          email: user?.email,
          coupon: dataConstants.PLAN_CYCLES.ANNUALLY === planCycle ? (coupon?.id ?? coupon?.code) : '',
          oldUser: ((stripe_customer_id && userType === "FREE") || hasPastSubscriptions),
        });
        stripe === null || stripe === void 0 ? void 0 : stripe.redirectToCheckout({ sessionId });
      }
    } catch (error) {
      return alert(error === null || error === void 0 ? void 0 : error.message);
    } finally {
      setPriceIdLoading(undefined);
    }
  };

  const handleCoupon = (prop) => (event) => {
    delete coupon?.m_final_amount;
    delete coupon?.y_final_amount;
    setCoupon({
      ...coupon,
      [prop]: event.target.value,
      loading: false,
      isValid: false,
    });
  };

  const handleCouponCode = async () => {
    await timeout(1000);
    setShowCoupon(coupon?.errText ? false : true);
  };

  const enterCoupon = () => {
    setCoupon({ ...coupon, hasCoupon: !coupon.hasCoupon });
  };

  const getCoupon = async (event) => {
    event.preventDefault();
    setCoupon({ ...coupon, loading: true });
    await fetch(`${process.env.NEXT_PUBLIC_PAY_URL}/promotion?code=${encodeURIComponent(coupon?.code)}`)
      .then((res) => {
        if (!res.ok) {
          return res.text().then(text => {
            setCoupon({ ...coupon, errText: text });
            throw new Error(text);
          });
        }
        return res.json();
      })
      .then((d) => {
        const data = d?.data[0];
        d?.data <= 0 ?
          setCoupon({ ...coupon, errText: 'Not Found' }) :
          setCoupon({
            ...coupon,
            ...data?.coupon,
            isValid: data?.coupon?.id === coupon?.code ? data?.coupon?.valid ?? false : false,
            loading: false,
            m_final_amount: Math.round((((planItem && currency_type === "inr" ? planItem?.iy : planItem?.y) / 12) * data?.coupon?.percent_off) / 100) ?? null,
            y_final_amount: Math.round((planItem && currency_type === "inr" ? planItem?.iy : planItem?.y) * (1 - (data?.coupon?.percent_off / 100))),
          });
        handleCouponCode(data && d?.data <= 0 ? false : true);
      })
      .catch(err => {
        console.log('caught it!', err);
      });
  };

  const getCurrencySymbol = (ct) => {
    switch (ct) {
      case "inr": return "\u20B9";
      case "gbp": return "\u00A3";
      case "eur": return "\u20AC";
      default: return "$";
    }
  };

  const symbol = getCurrencySymbol(currency_type);

  useEffect(() => {
    if (typeof window === 'undefined' || !email || stripe_customer_id) return;
    const controller = new AbortController();
    checkSubscription(email)
      .then(d => { if (!controller.signal.aborted) setPastSubscriptions(d?.hasPastSubscriptions); })
      .catch(() => { /* subscription check is optional — fail silently */ });
    return () => controller.abort();
  }, [stripe_customer_id, email]);

  const isAnnual = planCycle === "Annually";
  const activePriceArr = isAnnual ? splitPriceAnnually : splitPriceMonthly;
  const billedText = isAnnual
    ? `${symbol}${coupon?.y_final_amount >= 0 ? coupon.y_final_amount : planItem?.y} billed yearly`
    : `${symbol}${planItem?.m} billed monthly`;

  const savingsPercent = planItem?.m && planItem?.y
    ? Math.round(((planItem.m * 12 - planItem.y) / (planItem.m * 12)) * 100)
    : 0;

  return (
    <>
      <Box sx={{ textAlign: 'center', maxWidth: 400, mx: 'auto', mt: 2 }}>

        <Paper
          elevation={0}
          sx={{ display: 'inline-flex', justifyContent: 'center', mb: 4 }}
        >
          <StyledToggleButtonGroup
            size="small"
            value={planCycle}
            exclusive
            onChange={(event, newCycle) => { handleCycle(event, newCycle); handleBillingChange(event, newCycle); }}
            aria-label="plan-switch"
          >
            <ToggleButton value="Annually" aria-label="yearly-plan">
              Yearly
              {savingsPercent > 0 && (
                <Chip
                  label={`Save ${savingsPercent}%`}
                  size="small"
                  sx={{
                    ml: 1, height: 18, fontSize: '0.62rem', fontWeight: 700,
                    bgcolor: pricingTokens.badge.bg,
                    color: "#ffffff",
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              )}
            </ToggleButton>
            <ToggleButton value="Monthly" aria-label="monthly-plan">
              Monthly
            </ToggleButton>
          </StyledToggleButtonGroup>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            border: '2px solid',
            borderColor: pricingTokens.highlight,
            borderRadius: 3,
            p: { xs: 3, md: 4 },
            mb: 3,
            position: 'relative',
            bgcolor: '#fff',
            transition: 'box-shadow 0.3s ease',
            boxShadow: '0 4px 24px rgba(23, 78, 166, 0.08)',
          }}
        >
          {isAnnual && (
            <Chip
              label="MOST POPULAR"
              size="small"
              sx={{
                position: 'absolute',
                top: -12,
                left: '50%',
                transform: 'translateX(-50%)',
                bgcolor: pricingTokens.badge.bg,
                color: "#ffffff",
                fontWeight: 700,
                fontSize: '0.65rem',
                letterSpacing: '0.05em',
                height: 24,
                '& .MuiChip-label': { px: 1.5 },
              }}
            />
          )}

          <Typography variant="overline" sx={{
            fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em',
            color: 'text.secondary', display: 'block', mb: 1,
          }}>
            Premium Plan
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', mb: 0.5 }}>
            <Typography component="span" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary', mr: 0.25, alignSelf: 'flex-start', mt: 0.8 }}>
              {symbol}
            </Typography>
            <Typography component="span" sx={{ fontSize: '3.2rem', fontWeight: 800, letterSpacing: '-0.03em', color: 'text.primary', lineHeight: 1 }}>
              {activePriceArr ? Math.trunc(activePriceArr[0]) : '—'}
            </Typography>
            <Typography component="span" sx={{ fontSize: '1.2rem', fontWeight: 600, color: 'text.secondary', ml: 0.25 }}>
              {activePriceArr ? `.${activePriceArr[1] || '00'}` : ''}
            </Typography>
            <Typography component="span" sx={{ fontSize: '0.85rem', fontWeight: 500, color: 'text.secondary', ml: 0.5 }}>
              /mo
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.82rem', mb: 3 }}>
            {coupon?.y_final_amount >= 0 && isAnnual ? (
              <>
                <Typography component="del" variant="inherit" sx={{ color: 'text.disabled', mr: 0.5 }}>
                  {symbol}{planItem?.y}
                </Typography>
                <Typography component="span" variant="inherit" sx={{ color: '#15803d', fontWeight: 600 }}>
                  {symbol}{coupon.y_final_amount} billed yearly
                </Typography>
              </>
            ) : billedText}
          </Typography>

          <Button
            variant="contained"
            fullWidth
            data-event-name="checkout-btn"
            data-event-category="homepage"
            data-event-label={planCycle}
            disabled={!!priceIdLoading}
            sx={{
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 700,
              py: 1.5,
              mt: 1.5,
              borderRadius: 2,
              bgcolor: pricingTokens.cta.bg,
              color: pricingTokens.cta.text,
              boxShadow: '0 2px 12px rgba(23, 78, 166, 0.2)',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: pricingTokens.cta.hover,
                boxShadow: '0 4px 20px rgba(23, 78, 166, 0.3)',
                transform: 'translateY(-1px)',
              },
              '&:active': { transform: 'translateY(0)' },
            }}
            onClick={(event) => {
              handleCheckout(priceID);
              send.uevent(event);
            }}
          >
            {priceIdLoading ? (
              <CircularProgress size={22} sx={{ color: '#fff' }} />
            ) : isPremium ? 'Manage Subscription' : ((stripe_customer_id && userType === "FREE") || hasPastSubscriptions) ? 'Buy Subscription' : 'Buy Subscription'}
          </Button>

          <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'text.secondary', fontSize: '0.72rem' }}>
            Cancel anytime &middot; Secure checkout via Stripe
          </Typography>
        </Paper>

        <Box sx={{ minHeight: 48, mb: 1 }}>
          {showCoupon ? (
            <Box sx={{ textAlign: 'center' }}>
              <Chip
                sx={{
                  borderRadius: 2,
                  '& .MuiChip-label': { fontWeight: 600, mx: 1, fontSize: '0.8rem' },
                  bgcolor: !isAnnual ? 'error.50' : 'success.50',
                }}
                icon={<SellRoundedIcon sx={{ fontSize: '0.9rem !important' }} />}
                label={coupon?.code ?? ''}
                onDelete={() => {
                  setCoupon({ hasCode: false, code: '', loading: false, isValid: false });
                  setShowCoupon(false);
                }}
                deleteIcon={<ClearRoundedIcon sx={{ fontSize: '1rem !important' }} />}
              />
              <FormHelperText sx={{
                color: !isAnnual || coupon?.errText ? 'error.main' : '#15803d',
                textAlign: 'center', mt: 0.5,
              }}>
                {!isAnnual ? 'Valid only on yearly plan' : (coupon?.name ?? coupon?.errText)}
              </FormHelperText>
            </Box>
          ) : !coupon.hasCoupon ? (
            <Button
              onClick={enterCoupon}
              disabled={!isAnnual}
              sx={{
                textTransform: 'none', color: 'primary.main', fontWeight: 500,
                fontSize: '0.82rem', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
              }}
            >
              Have a coupon code?
            </Button>
          ) : (
            <Box sx={{ maxWidth: 260, mx: 'auto' }}>
              <ClickAwayListener
                mouseEvent="onMouseDown"
                touchEvent="onTouchStart"
                onClickAway={(e) => coupon?.errText ? e.preventDefault() : enterCoupon()}
              >
                <Paper
                  elevation={0}
                  component="form"
                  sx={{
                    p: '3px 4px', display: 'flex', alignItems: 'center',
                    border: '1px solid', borderColor: 'grey.300', borderRadius: 2,
                  }}
                  onSubmit={getCoupon}
                >
                  <InputBase
                    id="coupon-code"
                    sx={{ ml: 1.5, flex: 1, fontSize: '0.85rem' }}
                    placeholder="Enter code"
                    value={coupon.code}
                    onChange={handleCoupon('code')}
                    inputProps={{ 'aria-label': 'coupon code' }}
                  />
                  <Divider sx={{ height: 24, mx: 0.5, borderColor: 'grey.200' }} orientation="vertical" />
                  {coupon?.valid ? (
                    <IconButton color="success" size="small" aria-label="verified">
                      <DoneRoundedIcon fontSize="small" />
                    </IconButton>
                  ) : coupon?.loading ? (
                    <CircularProgress size={20} sx={{ mx: 1 }} />
                  ) : (
                    <Button
                      size="small"
                      sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', minWidth: 'auto', px: 1 }}
                      onClick={getCoupon}
                      type="submit"
                    >
                      Apply
                    </Button>
                  )}
                </Paper>
              </ClickAwayListener>
              <FormHelperText sx={{
                color: coupon?.valid ? '#15803d' : coupon?.errText ? 'error.main' : 'text.secondary',
                textAlign: 'center', mt: 0.5, fontSize: '0.72rem',
              }}>
                {coupon?.valid ? 'Coupon applied successfully' : (coupon?.errText ?? 'Press Enter to apply')}
              </FormHelperText>
            </Box>
          )}
        </Box>
      </Box>

      <Dialog
        open={pausedDialogOpen}
        onClose={() => setPausedDialogOpen(false)}
        fullScreen={isMobile}
        fullWidth
        maxWidth="xs"
        aria-labelledby="subscriptions-paused-title"
        aria-describedby="subscriptions-paused-description"
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            boxShadow: '0 12px 40px rgba(0,0,0,0.14)',
          },
        }}
      >
        <IconButton
          onClick={() => setPausedDialogOpen(false)}
          aria-label="Close"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            color: 'grey.400',
            '&:hover': { color: 'grey.700', bgcolor: 'grey.100' },
          }}
        >
          <ClearRoundedIcon fontSize="small" />
        </IconButton>

        <DialogContent
          sx={{
            textAlign: 'center',
            px: { xs: 3.5, sm: 5 },
            py: { xs: 6, sm: 6 },
          }}
        >
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              bgcolor: 'primary.50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <PauseCircleRoundedIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          </Box>

          <Typography
            id="subscriptions-paused-title"
            variant="h6"
            sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}
          >
            New subscriptions are paused
          </Typography>

          <Typography
            id="subscriptions-paused-description"
            variant="body2"
            sx={{ color: 'text.secondary', lineHeight: 1.65, mb: 4 }}
          >
            Thanks so much for your interest in Grufity!
          </Typography>
          <Typography
            id="subscriptions-paused-description"
            variant="body2"
            sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.65, mb: 4, mt: 1.5 }}
          >
            We're temporarily pausing new sign-ups while we make things even better.
            We'd love to have you on board, so please check back in a little while.
          </Typography>

          <Button
            variant="contained"
            fullWidth
            disableElevation
            onClick={() => setPausedDialogOpen(false)}
            sx={{
              py: 1.4,
              fontWeight: 700,
              fontSize: '0.95rem',
              textTransform: 'none',
              borderRadius: 2,
              bgcolor: pricingTokens.cta.bg,
              color: pricingTokens.cta.text,
              '&:hover': { bgcolor: pricingTokens.cta.hover },
            }}
          >
            Got it
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LandingCheckout;
